import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { BarChart3, Gauge } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, ErrorState, Skeleton } from '@/components/States'
import { fetchAllTickets, type AdminTicket, type AdminStaff } from '@/lib/adapters'
import { safeList, fetchSupportKpi, type SupportKpi, TK_STR as S } from './ticketsApi'

const isActive = (t: AdminTicket) => t.status === 'open' || t.status === 'pending'

interface Load {
  key: string
  name: string
  count: number
}

/** A7.23 — Jamoa yuklamasi. Ochiq tiketlar agent bo'yicha (JONLI assigned_to). */
export function TeamWorkload() {
  const [rows, setRows] = useState<AdminTicket[] | null>(null)
  const [staff, setStaff] = useState<AdminStaff[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setError(null)
    fetchAllTickets(ac.signal)
      .then((r) => alive && setRows(r))
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
    safeList<AdminStaff>('/admin/staff', 'staff', ac.signal).then((s) => alive && setStaff(s))
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const { loads, totalOpen } = useMemo(() => {
    const active = (rows ?? []).filter(isActive)
    const nameOf = (uid: number) => staff.find((s) => s.id === uid)?.username ?? `#${uid}`
    const m = new Map<string, Load>()
    let unassigned = 0
    for (const t of active) {
      if (t.assigned_to == null) {
        unassigned++
        continue
      }
      const key = String(t.assigned_to)
      const cur = m.get(key) ?? { key, name: nameOf(t.assigned_to), count: 0 }
      cur.count++
      m.set(key, cur)
    }
    const loads = [...m.values()].sort((a, b) => b.count - a.count)
    if (unassigned > 0) loads.push({ key: '_un', name: S.unassigned, count: unassigned })
    return { loads, totalOpen: active.length }
  }, [rows, staff])

  const max = Math.max(1, ...loads.map((l) => l.count))
  const loading = rows === null

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  return (
    <>
      <PageHeader title={S.workload} screenId="A7.23" description={S.workloadNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-3 flex flex-wrap gap-2">
          <Tile label={S.totalOpen} value={loading ? undefined : totalOpen} accent />
          <Tile label={S.agentsCount} value={loading ? undefined : loads.filter((l) => l.key !== '_un').length} />
        </div>
        {loading ? (
          <div className="max-w-xl space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
        ) : loads.length === 0 ? (
          <EmptyState title={S.empty} icon={<BarChart3 size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="max-w-xl space-y-1.5">
            {loads.map((l) => (
              <div key={l.key} className="flex items-center gap-3 text-sm">
                <span className="w-32 shrink-0 truncate text-secondary">{l.name}</span>
                <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-3">
                  <div className={`h-full rounded ${l.key === '_un' ? 'bg-warning/60' : 'bg-accent/70'}`} style={{ width: `${Math.max(3, (l.count / max) * 100)}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right font-mono tabular text-primary">{l.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/** A7.24 — Tiket hisobotlari. JONLI GET /admin/analytics/support (A16.38 KPI). */
export function TicketReports() {
  const [data, setData] = useState<SupportKpi | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchSupportKpi(ac.signal)
      .then(setData)
      .finally(() => !ac.signal.aborted && setLoading(false))
    return () => ac.abort()
  }, [])

  const maxDay = data ? Math.max(1, ...data.new_per_day.map((d) => d.count)) : 1
  const empty = !loading && (!data || data.total === 0)

  return (
    <>
      <PageHeader title={S.reports} screenId="A7.24" description={S.reportsNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : empty ? (
          <EmptyState title={S.greenfield} icon={<Gauge size={28} strokeWidth={1.5} />} />
        ) : (
          data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <Tile label={S.total} value={data.total} accent />
                <Tile label={S.openLbl} value={data.open} warn={data.open > 0} />
                <Tile label={S.pendingLbl} value={data.pending} />
                <Tile label={S.closedLbl} value={data.closed} />
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <Tile label={S.new7d} value={data.new_7d} />
                <Tile label={S.closed7d} value={data.closed_7d} />
                <Tile label={S.avgRes} value={`${data.avg_resolution_hours.toFixed(1)} ${S.hours}`} />
                <Tile label={S.medianRes} value={`${data.median_resolution_hours.toFixed(1)} ${S.hours}`} />
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <BarPanel title={S.byCategory} data={data.by_category} />
                <BarPanel title={S.byPriority} data={data.by_priority} />
              </div>

              {data.new_per_day.length > 0 && (
                <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="mb-2 text-sm font-medium text-secondary">{S.newPerDay}</div>
                  <div className="flex h-28 items-end gap-0.5">
                    {data.new_per_day.map((d) => (
                      <div key={d.day} className="flex-1" title={`${d.day}: ${d.count}`}>
                        <div className="rounded-t bg-accent/70" style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </>
  )
}

function BarPanel({ title, data }: { title: string; data: { key: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count))
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {data.length === 0 ? (
        <div className="py-4 text-center text-sm text-tertiary">{S.empty}</div>
      ) : (
        <div className="space-y-1.5">
          {data.map((d) => (
            <div key={d.key} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 truncate text-secondary">{d.key}</span>
              <div className="relative h-4 flex-1 overflow-hidden rounded bg-surface-3">
                <div className="h-full rounded bg-accent/70" style={{ width: `${Math.max(2, (d.count / max) * 100)}%` }} />
              </div>
              <span className="w-12 shrink-0 text-right font-mono text-sm tabular text-primary">{d.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, accent, warn }: { label: string; value: number | string | undefined; accent?: boolean; warn?: boolean }) {
  const tone = warn ? 'text-warning' : accent ? 'text-accent' : 'text-primary'
  const shown: ReactNode = value === undefined ? <Skeleton className="mt-0.5 h-7 w-16" /> : <div className={`mt-0.5 font-mono text-2xl tabular ${tone}`}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
  return (
    <div className="min-w-32 rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {shown}
    </div>
  )
}
